import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  listReportTypes,
  getReportSchema,
  exportDocx,
  exportPdf,
  downloadBlob,
} from "../../utils/reportsApi";
import { TableField, FixedTableField } from "./fields/TableField";
import { DomainGroupField } from "./fields/DomainGroupField";
import {
  NumberedListField,
  SignoffField,
  InfoBox,
} from "./fields/SimpleFields";
import "./ReportBuilder.css";

const DRAFT_KEY_PREFIX = "ahc-report-draft:";

const LEVEL_TONE = {
  "Level A": "a",
  "Level B": "b",
  "Level C": "c",
  "Level D": "d",
};

function loadDraft(type) {
  try {
    const raw = localStorage.getItem(DRAFT_KEY_PREFIX + type);
    return raw ? JSON.parse(raw) : { meta: {}, sections: {} };
  } catch {
    return { meta: {}, sections: {} };
  }
}

function saveDraft(type, data) {
  try {
    localStorage.setItem(DRAFT_KEY_PREFIX + type, JSON.stringify(data));
  } catch {
    /* storage unavailable — draft simply won't persist across reloads */
  }
}

function sectionIsStarted(section, sectionData) {
  if (!sectionData) return false;
  return section.fields.some((f) => {
    const v = sectionData[f.key];
    if (v === undefined || v === null) return false;
    if (Array.isArray(v))
      return (
        v.length > 0 &&
        v.some((item) =>
          typeof item === "string"
            ? item.trim()
            : Object.keys(item || {}).length,
        )
      );
    if (typeof v === "string") return v.trim().length > 0;
    return false;
  });
}

function FieldBlock({ field, value, onChange }) {
  switch (field.type) {
    case "text":
      return (
        <div className="rb-field">
          {field.label && (
            <label className="rb-field-label">{field.label}</label>
          )}
          <input
            className="rb-input"
            type="text"
            placeholder={field.placeholder}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
    case "select":
      return (
        <div className="rb-field">
          {field.label && (
            <label className="rb-field-label">{field.label}</label>
          )}
          <select
            className="rb-input"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="" />
            {field.options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      );
    case "textarea":
      return (
        <div className="rb-field">
          {field.label && (
            <label className="rb-field-label">{field.label}</label>
          )}
          <textarea
            className="rb-textarea"
            rows={5}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
    case "numberedList":
      return (
        <div className="rb-field">
          {field.label && (
            <label className="rb-field-label">{field.label}</label>
          )}
          <NumberedListField field={field} value={value} onChange={onChange} />
        </div>
      );
    case "table":
      return (
        <div className="rb-field">
          {field.label && (
            <label className="rb-field-label">{field.label}</label>
          )}
          <TableField field={field} value={value} onChange={onChange} />
        </div>
      );
    case "fixedTable":
      return (
        <div className="rb-field">
          {field.label && (
            <label className="rb-field-label">{field.label}</label>
          )}
          <FixedTableField field={field} value={value} onChange={onChange} />
        </div>
      );
    case "domainGroup":
      return (
        <DomainGroupField field={field} value={value} onChange={onChange} />
      );
    case "infobox":
      return <InfoBox field={field} />;
    case "signoff":
      return (
        <div className="rb-field">
          <SignoffField field={field} value={value} onChange={onChange} />
        </div>
      );
    default:
      return null;
  }
}

function MetaPanel({ schema, meta, onChange }) {
  return (
    <div className="rb-meta-panel">
      {schema.meta.map((f) => (
        <div className="rb-meta-field" key={f.key}>
          <label className="rb-field-label">{f.label}</label>
          {f.type === "select" ? (
            <select
              className="rb-input"
              value={meta[f.key] || ""}
              onChange={(e) => onChange(f.key, e.target.value)}
            >
              <option value="" />
              {f.options.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="rb-input"
              type="text"
              placeholder={f.placeholder}
              value={meta[f.key] || ""}
              onChange={(e) => onChange(f.key, e.target.value)}
            />
          )}
        </div>
      ))}
    </div>
  );
}

export default function ReportBuilder() {
  const [reportTypes, setReportTypes] = useState([]);
  const [reportType, setReportType] = useState(null);
  const [schema, setSchema] = useState(null);
  const [formData, setFormData] = useState({ meta: {}, sections: {} });
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [exporting, setExporting] = useState(null); // 'docx' | 'pdf' | null
  const [error, setError] = useState(null);
  const [loadingSchema, setLoadingSchema] = useState(false);

  useEffect(() => {
    listReportTypes()
      .then((types) => {
        setReportTypes(types);
        if (types.length) setReportType(types[0].type);
      })
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    if (!reportType) return;
    setLoadingSchema(true);
    setError(null);
    getReportSchema(reportType)
      .then((s) => {
        setSchema(s);
        setFormData(loadDraft(reportType));
        setActiveSectionId(s.sections[0]?.id || null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoadingSchema(false));
  }, [reportType]);

  useEffect(() => {
    if (reportType && schema) saveDraft(reportType, formData);
  }, [reportType, schema, formData]);

  const updateMeta = useCallback((key, value) => {
    setFormData((prev) => ({ ...prev, meta: { ...prev.meta, [key]: value } }));
  }, []);

  const updateSectionField = useCallback((sectionId, fieldKey, value) => {
    setFormData((prev) => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionId]: { ...prev.sections[sectionId], [fieldKey]: value },
      },
    }));
  }, []);

  const activeSection = useMemo(
    () => schema?.sections.find((s) => s.id === activeSectionId) || null,
    [schema, activeSectionId],
  );

  const activeIndex = useMemo(
    () =>
      schema ? schema.sections.findIndex((s) => s.id === activeSectionId) : -1,
    [schema, activeSectionId],
  );

  const goTo = (idx) => {
    if (!schema) return;
    const clamped = Math.max(0, Math.min(schema.sections.length - 1, idx));
    setActiveSectionId(schema.sections[clamped].id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleExport = async (kind) => {
    if (!schema) return;
    setExporting(kind);
    setError(null);
    try {
      const fn = kind === "docx" ? exportDocx : exportPdf;
      const { blob, filename } = await fn(schema.type, formData);
      downloadBlob(blob, filename);
    } catch (e) {
      setError(e.message);
    } finally {
      setExporting(null);
    }
  };

  const levelValue = formData.meta.overallLevel;
  const scoreValue = formData.meta.compositeScore;

  return (
    <div className="rb-page">
      <header className="rb-topbar">
        <div className="rb-topbar-left">
          <span className="rb-brand-eyebrow">
            Africa Health Collaborative · Addis Ababa University
          </span>
          <h1 className="rb-brand-title">
            PHC Capacity Assessment — Report Builder
          </h1>
        </div>
        <div className="rb-topbar-right">
          <label className="rb-type-select-label" htmlFor="rb-type-select">
            Report type
          </label>
          <select
            id="rb-type-select"
            className="rb-type-select"
            value={reportType || ""}
            onChange={(e) => setReportType(e.target.value)}
          >
            {reportTypes.map((t) => (
              <option key={t.type} value={t.type}>
                {t.reportLabel}
              </option>
            ))}
          </select>
        </div>
      </header>

      {error && (
        <div className="rb-error-banner" role="alert">
          {error}
        </div>
      )}

      {loadingSchema || !schema ? (
        <div className="rb-loading">Loading report structure…</div>
      ) : (
        <div className="rb-body">
          <aside className="rb-sidebar">
            <p className="rb-sidebar-tagline">{schema.tagline}</p>
            {levelValue && (
              <div
                className={`rb-level-chip rb-level-${LEVEL_TONE[levelValue] || "d"}`}
              >
                <span className="rb-level-chip-label">{levelValue}</span>
                {scoreValue && (
                  <span className="rb-level-chip-score">
                    {scoreValue} / 1000
                  </span>
                )}
              </div>
            )}
            <MetaPanel
              schema={schema}
              meta={formData.meta}
              onChange={updateMeta}
            />

            <nav className="rb-section-nav" aria-label="Report sections">
              {schema.sections.map((section, idx) => {
                const started = sectionIsStarted(
                  section,
                  formData.sections[section.id],
                );
                return (
                  <button
                    type="button"
                    key={section.id}
                    className={`rb-nav-item ${section.id === activeSectionId ? "rb-nav-item-active" : ""}`}
                    onClick={() => goTo(idx)}
                  >
                    <span
                      className={`rb-nav-dot ${started ? "rb-nav-dot-filled" : ""}`}
                      aria-hidden="true"
                    />
                    {section.title}
                  </button>
                );
              })}
            </nav>

            <div className="rb-export-block">
              <button
                type="button"
                className="rb-btn rb-btn-primary"
                disabled={!!exporting}
                onClick={() => handleExport("docx")}
              >
                {exporting === "docx"
                  ? "Preparing Word file…"
                  : "Export as Word (.docx)"}
              </button>
              <button
                type="button"
                className="rb-btn rb-btn-secondary"
                disabled={!!exporting}
                onClick={() => handleExport("pdf")}
              >
                {exporting === "pdf" ? "Preparing PDF…" : "Export as PDF"}
              </button>
              <p className="rb-autosave-note">
                Draft saves automatically in this browser.
              </p>
            </div>
          </aside>

          <main className="rb-panel">
            {schema.guidance && (
              <InfoBox
                field={{
                  title: "How to use this report",
                  body: schema.guidance,
                }}
              />
            )}

            {activeSection && (
              <section className="rb-section-card">
                <h2 className="rb-section-title">{activeSection.title}</h2>
                {activeSection.howTo && (
                  <p className="rb-howto">{activeSection.howTo}</p>
                )}

                {activeSection.fields.map((field) => (
                  <FieldBlock
                    key={field.key}
                    field={field}
                    value={
                      (formData.sections[activeSection.id] || {})[field.key]
                    }
                    onChange={(v) =>
                      updateSectionField(activeSection.id, field.key, v)
                    }
                  />
                ))}

                <div className="rb-section-footer">
                  <button
                    type="button"
                    className="rb-btn rb-btn-ghost"
                    onClick={() => goTo(activeIndex - 1)}
                    disabled={activeIndex <= 0}
                  >
                    ← Previous section
                  </button>
                  <span className="rb-section-progress">
                    Section {activeIndex + 1} of {schema.sections.length}
                  </span>
                  <button
                    type="button"
                    className="rb-btn rb-btn-ghost"
                    onClick={() => goTo(activeIndex + 1)}
                    disabled={activeIndex >= schema.sections.length - 1}
                  >
                    Next section →
                  </button>
                </div>
              </section>
            )}
          </main>
        </div>
      )}
    </div>
  );
}
